import { useMemo } from "react";
import { useDevicesQuery } from "./devicesQuery";
import { useQueries } from "@tanstack/react-query";
import { PaymentPlanState } from "../models/DevicePaymentPlan";
import { DevicePaymentPlan } from "../models/DevicePaymentPlan";
import { Device } from "../models/Device";
import OCGatewayClient from "../client/OCGatewayClient";

/**
 * Hook that returns true if the current user has at least one device
 * with a required payment plan.
 *
 * Derives result from existing device and payment plan queries without
 * adding new global services.
 */
export function useHasRequiredDevicePaymentPlan(): boolean {
  const devicesQuery = useDevicesQuery();

  // Flatten the infinite query pages into a simple array of devices
  const devices = useMemo(() => {
    return (
      devicesQuery.data?.pages.reduce((acc, page) => acc.concat(page), []) ?? []
    );
  }, [devicesQuery.data]);

  // Use useQueries to handle dynamic number of payment plan queries
  // These queries fetch without complex subscriptions - they just get the current state
  const paymentPlanQueries = useQueries({
    queries: devices.map((device: Device) => ({
      queryKey: ["getDevicePaymentPlan", device.deviceId],
      queryFn: async (): Promise<DevicePaymentPlan | null> => {
        try {
          const result = await OCGatewayClient.request("getDevicePaymentPlan", {
            deviceId: device.deviceId,
            subscribe: true,
          });
          return result ? new DevicePaymentPlan(result) : null;
        } catch (error) {
          console.error(
            "Error fetching payment plan for",
            device.deviceId,
            error,
          );
          return null;
        }
      },
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60 * 24,
      networkMode: "always",
      retry: false,
    })),
  });

  // Check if any device has a required payment plan
  const hasRequired = useMemo(() => {
    return paymentPlanQueries.some(
      (query: any) =>
        (query.data as DevicePaymentPlan)?.state === PaymentPlanState.Required,
    );
  }, [paymentPlanQueries]);

  return hasRequired;
}
